import React, { FC } from 'react';
import { Button, Box, IconButton, Dialog, Stack, Typography } from '@mui/material';
import { X } from 'react-feather';

import type { Procedure } from '../../types/common';
import { LocationPicker } from './pickers/LocationPicker';
import { CapacityFilterPicker } from './pickers/CapacityFilterPicker';
import { EventDatePicker } from './pickers/EventDatePicker';
import { EventTimePicker } from './pickers/EventTimePicker';
import { useFilterBarContext } from './providers/FilterBarProvider';

interface MobileFilterBarDialogProps {
  isOpen: boolean;
  onClose: Procedure;
  hasLocationSearch: boolean;
}

export const MobileFilterBarDialog: FC<MobileFilterBarDialogProps> = ({
  isOpen,
  onClose,
  hasLocationSearch,
}: MobileFilterBarDialogProps) => {
  const {
    locationQuery,
    onChangeLocationQuery,
    eventDate,
    onChangeEventDate,
    startTime,
    onChangeStartTime,
    clearStartTime,
    endTime,
    onChangeEndTime,
    clearEndTime,
    capacityFilterValues,
    onSubmitCapacityFilter,
    onCancelCapacityFilter,
    onClearFilters,
    expressBookSettings,
    hoursOfOperation,
  } = useFilterBarContext();
  const [updatedCapacityFilterValues, setUpdatedCapacityFilterValues] =
    React.useState(capacityFilterValues);
    
  const handleClearSelection = () => {
    // Reset local capacity filter state to default cleared values
    const clearedCapacityValues = {
      minGuests: undefined,
      maxGuests: undefined,
      allowSeated: false,
      allowStanding: false,
    };
    setUpdatedCapacityFilterValues(clearedCapacityValues);
    
    if (onClearFilters) {
      // Search mode - use the provided bulk clear function
      onClearFilters();
    } else {
      // Profile mode - clear fields individually
      onChangeEventDate(null);
      clearStartTime();
      clearEndTime();
      onCancelCapacityFilter();
    }
  };

  const handleApplyFilter = () => {
    if (onSubmitCapacityFilter) {
      onSubmitCapacityFilter(updatedCapacityFilterValues);
      onClose();
    }
  };

  const renderDialogHeader = () => (
    <Stack
      direction="row"
      gap={ "20px"}
      alignItems="center"
      justifyContent="flexStart" 
      sx={{ margin: '16px 20px', width: '100%'}}
    >
      <IconButton onClick={onClose} title="close">
        <X />
      </IconButton>
      <Typography variant="h6" sx={{ 
        width: '100%', 
        textAlign: 'left'
      }}>
        Filter
      </Typography>
    </Stack>
  );

  const renderDialogFooter = () => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      padding="24px"
      sx={{ boxShadow: '0px 1px 0px 0px rgba(0, 0, 0, 0.10) inset' }}
    >
      <Button
        color="primary"
        variant="outlined"
        sx={{ textTransform: 'none' }}
        onClick={handleClearSelection}
      >
        Clear Selections
      </Button>
      <Button
        color="secondary"
        variant="contained"
        sx={{ textTransform: 'none' }}
        onClick={handleApplyFilter}
      >
        Apply Filter
      </Button>
    </Stack>
  );

  const renderLocationSection = () => {
    if (!hasLocationSearch) return null;
    
    return (
      <Stack direction="column" gap="16px" width="100%" maxWidth="368px">
        <Typography variant="h6">Location</Typography>
        <LocationPicker
          locationQuery={locationQuery || ''}
          onChangeLocationQuery={onChangeLocationQuery || (() => {})}
          styleProps={{ height: '56px' }}
          shouldShowOutlineSelector
          shouldRenderIcon
        />
      </Stack>
    );
  };

  const renderDialogBody = () => (
    <Stack direction="column" gap="24px" padding="24px" alignItems="center">
      {renderLocationSection()}
      
      <Stack direction="column" gap="16px" maxWidth={'368px'} width="100%">
        <Typography variant="h6">Select Date</Typography>
        <EventDatePicker
          eventDate={eventDate}
          onChangeEventDate={onChangeEventDate}
          onClose={onClose}
          shouldRenderActionBar={false}
          shouldCloseOnChange={false}
          expressBookSettings={expressBookSettings}
          hoursOfOperation={hoursOfOperation}
        />
      </Stack>
      
      <Box maxWidth="368px" width="100%">
        <EventTimePicker
          eventDate={eventDate}
          startTime={startTime}
          endTime={endTime}
          onChangeStartTime={onChangeStartTime}
          onChangeEndTime={onChangeEndTime}
          clearStartTime={clearStartTime}
          clearEndTime={clearEndTime}
          onClose={onClose}
          shouldRenderActionBar={false}
          expressBookSettings={expressBookSettings}
          hoursOfOperation={hoursOfOperation}
        />
      </Box>
      
      <Stack direction="column" gap="16px" width="100%" maxWidth="368px">
        <Typography variant="h6">Guests</Typography>
        <CapacityFilterPicker
          initialValues={capacityFilterValues}
          onChange={setUpdatedCapacityFilterValues}
        />
      </Stack>
    </Stack>
  );

  return (
    <Dialog open={isOpen} onClose={onClose} fullScreen>
      <Stack 
        direction="column" 
        justifyContent="space-between" 
        height="100%"
      >
        <Stack direction="column">
          {renderDialogHeader()}
          {renderDialogBody()}
        </Stack>
        {renderDialogFooter()}
      </Stack>
    </Dialog>
  );
};
